import React, { useState, useEffect, useRef } from 'react'
import {
  Card, Row, Col, DatePicker, Button, Space, Table, Statistic, message, Select, Tag
} from 'antd'
import { DownloadOutlined, ReloadOutlined, BarChartOutlined, PieChartOutlined } from '@ant-design/icons'
import { analyticsApi } from '@/services/api'
import * as echarts from 'echarts'
import dayjs from 'dayjs'

const { RangePicker } = DatePicker
const { Option } = Select

interface ModelUsageData {
  model: string
  model_name: string
  provider: string
  usage_count: number
  conversation_count: number
  total_tokens: number
  avg_tokens_per_message: number
  usage_percentage: number
  first_used: number
  last_used: number
}

const ModelUsagePage: React.FC = () => {
  const [data, setData] = useState<ModelUsageData[]>([])
  const [loading, setLoading] = useState(false)
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null)
  const [chartType, setChartType] = useState<'pie' | 'bar'>('pie')
  
  const pieChartRef = useRef<HTMLDivElement>(null)
  const barChartRef = useRef<HTMLDivElement>(null)
  const pieChartInstance = useRef<echarts.ECharts | null>(null)
  const barChartInstance = useRef<echarts.ECharts | null>(null)

  const fetchData = async () => {
    try {
      setLoading(true)
      const params = {
        startDate: dateRange?.[0]?.valueOf(),
        endDate: dateRange?.[1]?.valueOf(),
        limit: 20
      }

      const response = await analyticsApi.getModelUsageStats(params)
      if (response.success) {
        setData(response.data.models)
        updateCharts(response.data.models)
      } else {
        message.error('获取模型使用数据失败')
      }
    } catch (error) {
      console.error('获取模型使用数据失败:', error)
      message.error('获取模型使用数据失败')
    } finally {
      setLoading(false)
    }
  }

  const updateCharts = (modelData: ModelUsageData[]) => {
    console.log('更新图表，数据长度:', modelData.length, '图表类型:', chartType)

    // 更新饼图
    if (chartType === 'pie' && pieChartInstance.current && !pieChartInstance.current.isDisposed()) {
      console.log('更新饼图')
      const pieOption = {
        title: {
          text: '模型使用率分布',
          left: 'center'
        },
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          left: 'left'
        },
        series: [
          {
            name: '使用次数',
            type: 'pie',
            radius: '50%',
            data: modelData.map(item => ({
              value: item.usage_count || 0,
              name: item.model_name || '未知模型'
            })),
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            }
          }
        ]
      }
      pieChartInstance.current.setOption(pieOption, true) // 强制重新渲染
    }

    // 更新柱状图
    if (chartType === 'bar' && barChartInstance.current && !barChartInstance.current.isDisposed()) {
      console.log('更新柱状图')
      const barOption = {
        title: {
          text: '模型使用统计',
          left: 'center'
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        legend: {
          data: ['使用次数', 'Token数量'],
          top: 30
        },
        xAxis: {
          type: 'category',
          data: modelData.map(item => item.model_name || '未知模型'),
          axisLabel: {
            rotate: 45,
            interval: 0
          }
        },
        yAxis: [
          {
            type: 'value',
            name: '使用次数',
            position: 'left'
          },
          {
            type: 'value',
            name: 'Token数量',
            position: 'right'
          }
        ],
        series: [
          {
            name: '使用次数',
            type: 'bar',
            data: modelData.map(item => item.usage_count || 0),
            itemStyle: {
              color: '#1890ff'
            }
          },
          {
            name: 'Token数量',
            type: 'bar',
            yAxisIndex: 1,
            data: modelData.map(item => item.total_tokens || 0),
            itemStyle: {
              color: '#52c41a'
            }
          }
        ]
      }
      barChartInstance.current.setOption(barOption, true) // 强制重新渲染
    }
  }

  const initCharts = () => {
    console.log('初始化图表，当前图表类型:', chartType)
    // 确保DOM元素已经渲染
    setTimeout(() => {
      // 初始化饼图
      if (pieChartRef.current && !pieChartInstance.current) {
        console.log('初始化饼图实例')
        pieChartInstance.current = echarts.init(pieChartRef.current)
      }
      // 初始化柱状图
      if (barChartRef.current && !barChartInstance.current) {
        console.log('初始化柱状图实例')
        barChartInstance.current = echarts.init(barChartRef.current)
      }

      // 如果有数据，立即更新图表
      if (data.length > 0) {
        console.log('有数据，立即更新图表')
        updateCharts(data)
      }
    }, 100)
  }

  const handleExport = async () => {
    try {
      const params = {
        reportType: 'model_usage',
        format: 'csv',
        startDate: dateRange?.[0]?.valueOf(),
        endDate: dateRange?.[1]?.valueOf()
      }

      await analyticsApi.exportAnalyticsReport(params)
      message.success('导出成功')
    } catch (error) {
      console.error('导出失败:', error)
      message.error('导出失败')
    }
  }

  useEffect(() => {
    fetchData()

    // 延迟初始化图表，确保DOM已渲染
    const timer = setTimeout(() => {
      initCharts()
    }, 200)

    // 窗口大小改变时重新调整图表
    const handleResize = () => {
      pieChartInstance.current?.resize()
      barChartInstance.current?.resize()
    }

    window.addEventListener('resize', handleResize)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('resize', handleResize)
      if (pieChartInstance.current && !pieChartInstance.current.isDisposed()) {
        pieChartInstance.current.dispose()
      }
      if (barChartInstance.current && !barChartInstance.current.isDisposed()) {
        barChartInstance.current.dispose()
      }
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [dateRange])

  // 当数据更新时，更新图表
  useEffect(() => {
    console.log('数据或图表类型变化，数据长度:', data.length, '图表类型:', chartType)
    if (data.length > 0) {
      // 确保图表实例存在
      if (!pieChartInstance.current && pieChartRef.current) {
        pieChartInstance.current = echarts.init(pieChartRef.current)
      }
      if (!barChartInstance.current && barChartRef.current) {
        barChartInstance.current = echarts.init(barChartRef.current)
      }

      // 更新图表
      if (pieChartInstance.current || barChartInstance.current) {
        updateCharts(data)
      }
    }
  }, [data, chartType])

  const columns = [
    {
      title: '模型信息',
      key: 'model_info',
      width: 280,
      render: (_: any, record: ModelUsageData) => {
        const modelName = record.model_name || '未知模型'
        const configStatus = (record as any).config_status || 'configured'
        const isUnknownModel = (record as any).is_unknown_model || false
        const modelIdDisplay = (record as any).model_id_display || ''

        // 根据配置状态确定颜色和标签
        let color = '#262626'
        let tagColor = ''
        let tagText = ''

        if (configStatus === 'missing') {
          color = '#fa8c16'
          tagColor = 'orange'
          // 🔥 修复：显示具体的默认模型信息
          const defaultModel = 'claude-3.7-sonnet'  // 从proxy.js中获取的默认模型
          tagText = `使用默认模型 (${defaultModel})`
        } else if (configStatus === 'auto_generated') {
          color = '#fa8c16'
          tagColor = 'orange'
          tagText = isUnknownModel ? '数据异常' : '自动配置'
        } else if (isUnknownModel) {
          color = '#ff7875'
          tagColor = 'volcano'
          tagText = '数据异常'
        }

        return (
          <div>
            <div style={{
              fontWeight: 500,
              color: color,
              marginBottom: 4
            }}>
              {modelName}
            </div>
            {modelIdDisplay && modelIdDisplay !== modelName && (
              <div style={{
                fontSize: '12px',
                color: '#8c8c8c',
                fontFamily: 'monospace',
                marginBottom: 4
              }}>
                {modelIdDisplay.length > 30 ? `${modelIdDisplay.substring(0, 30)}...` : modelIdDisplay}
              </div>
            )}
            {/* 🔥 新增：显示模型配置信息 */}
            <div style={{ marginTop: 4 }}>
              {(record as any).provider && (
                <Tag color="blue">{(record as any).provider}</Tag>
              )}
              {(record as any).model_type && (
                <Tag color="green">{(record as any).model_type}</Tag>
              )}
              {(record as any).tier && (
                <Tag color="purple">{(record as any).tier}</Tag>
              )}
            </div>
            {/* 🔥 新增：显示模型能力 */}
            <div style={{ marginTop: 4 }}>
              {(record as any).supports_vision && <Tag color="orange">视觉</Tag>}
              {(record as any).supports_reasoning && <Tag color="red">推理</Tag>}
              {(record as any).supports_code && <Tag color="geekblue">代码</Tag>}
              {(record as any).supports_function_calling && <Tag color="cyan">函数</Tag>}
            </div>
            {tagText && (
              <Tag color={tagColor} style={{ fontSize: '11px', marginTop: 4 }}>
                {tagText}
              </Tag>
            )}
          </div>
        )
      }
    },
    {
      title: '提供商',
      dataIndex: 'provider',
      key: 'provider',
      width: 120,
      render: (value: string) => {
        if (!value || value === 'unknown') {
          return <span style={{ color: '#999' }}>未知</span>
        }
        return <Tag color="blue">{value}</Tag>
      }
    },
    {
      title: '使用次数',
      dataIndex: 'usage_count',
      key: 'usage_count',
      width: 100,
      align: 'right' as const,
      render: (value: any) => {
        const numValue = Number(value) || 0;
        return numValue.toLocaleString();
      }
    },
    {
      title: '会话数',
      dataIndex: 'conversation_count',
      key: 'conversation_count',
      width: 100,
      align: 'right' as const,
      render: (value: any) => {
        const numValue = Number(value) || 0;
        return numValue.toLocaleString();
      }
    },
    {
      title: 'Token总数',
      dataIndex: 'total_tokens',
      key: 'total_tokens',
      width: 120,
      align: 'right' as const,
      render: (value: any) => {
        const numValue = Number(value) || 0;
        return numValue.toLocaleString();
      }
    },
    {
      title: '平均Token',
      dataIndex: 'avg_tokens_per_message',
      key: 'avg_tokens_per_message',
      width: 100,
      align: 'right' as const,
      render: (value: any) => {
        const numValue = Number(value) || 0;
        return Math.round(numValue).toLocaleString();
      }
    },
    {
      title: '使用率',
      dataIndex: 'usage_percentage',
      key: 'usage_percentage',
      width: 100,
      align: 'right' as const,
      render: (value: any) => {
        const numValue = Number(value) || 0;
        return `${numValue.toFixed(1)}%`;
      }
    },
    {
      title: '首次使用',
      dataIndex: 'first_used',
      key: 'first_used',
      width: 120,
      render: (timestamp: number) => {
        if (!timestamp) return <span style={{ color: '#999' }}>-</span>
        return dayjs(timestamp).format('MM-DD HH:mm')
      }
    },
    {
      title: '最后使用',
      dataIndex: 'last_used',
      key: 'last_used',
      width: 120,
      render: (timestamp: number) => {
        if (!timestamp) return <span style={{ color: '#999' }}>-</span>
        return dayjs(timestamp).format('MM-DD HH:mm')
      }
    }
  ]

  const totalUsage = data.reduce((sum, item) => sum + (Number(item.usage_count) || 0), 0)
  const totalTokens = data.reduce((sum, item) => sum + (Number(item.total_tokens) || 0), 0)
  const totalConversations = data.reduce((sum, item) => sum + (Number(item.conversation_count) || 0), 0)

  // 配置状态统计
  const configuredModels = data.filter(m => (m as any).config_status === 'configured').length
  const autoGeneratedModels = data.filter(m => (m as any).config_status === 'auto_generated').length
  const missingConfigModels = data.filter(m => (m as any).config_status === 'missing').length
  const unknownModelData = data.find(m => (m as any).is_unknown_model)
  const unknownModelUsage = unknownModelData ? Number(unknownModelData.usage_count) || 0 : 0
  const unknownModelPercentage = totalUsage > 0 ? ((unknownModelUsage / totalUsage) * 100).toFixed(1) : '0'

  return (
    <div style={{ padding: '0' }}>
      <Card title="模型使用率分析" extra={
        <Space>
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
            placeholder={['开始日期', '结束日期']}
          />
          <Select
            value={chartType}
            onChange={setChartType}
            style={{ width: 120 }}
          >
            <Option value="pie">
              <PieChartOutlined /> 饼图
            </Option>
            <Option value="bar">
              <BarChartOutlined /> 柱状图
            </Option>
          </Select>
          <Button icon={<ReloadOutlined />} onClick={fetchData}>
            刷新
          </Button>
          <Button type="primary" icon={<DownloadOutlined />} onClick={handleExport}>
            导出
          </Button>
        </Space>
      }>
        {/* 统计概览 */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Statistic
              title="总使用次数"
              value={totalUsage}
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="总会话数"
              value={totalConversations}
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="总Token数"
              value={totalTokens}
              valueStyle={{ color: '#722ed1' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="活跃模型数"
              value={data.length}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Col>
        </Row>

        {/* 配置状态统计 */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Statistic
              title="已配置模型"
              value={configuredModels}
              suffix={`/ ${data.length}`}
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="自动配置"
              value={autoGeneratedModels}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="使用默认模型"
              value={missingConfigModels}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="未识别数据"
              value={unknownModelUsage}
              suffix={`(${unknownModelPercentage}%)`}
              valueStyle={{ color: '#ff7875' }}
            />
          </Col>
        </Row>

        {/* 图表展示 */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={24}>
            <Card>
              {chartType === 'pie' ? (
                <div ref={pieChartRef} style={{ width: '100%', height: '400px' }} />
              ) : (
                <div ref={barChartRef} style={{ width: '100%', height: '400px' }} />
              )}
            </Card>
          </Col>
        </Row>

        {/* 详细数据表格 */}
        <Table
          columns={columns}
          dataSource={data}
          loading={loading}
          rowKey="model"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
          }}
          scroll={{ x: 1000 }}
        />
      </Card>
    </div>
  )
}

export default ModelUsagePage
